<script lang="ts">
  import type { EffortJsonldVueGlobalSearchDefaultReadSync } from '@/api'
  import type { MaybeRef } from 'vue'

  export interface ListDescriptionItemType {
    title: string
    titleClass?: CssClassType
    bothClass?: CssClassType
    lineClass?: CssClassType
    effortId?: number | EffortJsonldVueGlobalSearchDefaultReadSync | string
    effortLinkClass?: CssClassType
    description?: MaybeRef<string | null | number | boolean | undefined>
    tooltip?: string
    html?: boolean
    descriptionClass?: CssClassType
  }
</script>
<script lang="ts" setup>
  import { computed, onMounted, ref, toRefs } from 'vue'

  import EffortLink from '@/elements/EffortLink.vue'
  import { createIri, isIri } from '@/helpers/iri'
  import { useGeneratedEffortStore } from '@/stores'

  const props = withDefaults(defineProps<ListDescriptionItemType>(), {
    html: false,
    titleClass: undefined,
    bothClass: undefined,
    lineClass: undefined,
    effortId: undefined,
    effortLinkClass: undefined,
    description: undefined,
    tooltip: undefined,
    descriptionClass: undefined
  })

  const { effortId, effortLinkClass, bothClass, titleClass, title, descriptionClass, description, html, tooltip } = toRefs(props)

  const effortIri = computed(() => {
    if (!effortId.value) return undefined
    if (typeof effortId.value === 'number') return createIri(effortId.value, useGeneratedEffortStore().$iriPrefix)
    if (typeof effortId.value === 'string' && isIri(effortId.value)) return effortId.value
    if (effortId.value['@id']) return effortId.value['@id']
    return undefined
  })

  const computedTooltip = computed(() => {
    if (!tooltip.value) return undefined
    return { value: tooltip.value, modifiers: { bottom: true }, _x: 10 }
  })
  const autodescription = ref<string>()

  const descriptionAsHtml = computed(() => {
    if (!description.value) return undefined
    return description.value.toString().replace(/(\r\n|\n\r|\r|\n)/gi, '<br>')
  })
  onMounted(async () => {
    if (effortIri.value && !description.value) {
      const fetchedEffort = await useGeneratedEffortStore().fetchByIri(effortIri.value)
      autodescription.value = fetchedEffort.value?.title ?? undefined
    }
  })
</script>
<template>
  <h4 :class="['text-sm font-medium leading-6', bothClass ?? [], titleClass ?? []]">
    <span
      :v-tooltip="computedTooltip"
      :class="[{ 'underline decoration-dashed': tooltip }]">
      {{ title }}
    </span>
  </h4>
  <EffortLink
    v-if="effortId"
    :id="effortId"
    :class="[bothClass ?? [], effortLinkClass ?? []]">
    <span
      v-if="description || autodescription"
      :class="['text-sm', bothClass ?? [], descriptionClass ?? []]">
      <slot v-if="description">{{ description }}</slot>
      <slot v-else>{{ autodescription }}</slot>
    </span>
  </EffortLink>
  <span
    v-else-if="description && !html"
    :class="['text-sm', bothClass ?? [], descriptionClass ?? []]">
    <slot>{{ description }}</slot>
  </span>
  <div
    v-else-if="description && html"
    v-titrop-html="descriptionAsHtml"
    :class="['text-sm', bothClass ?? [], descriptionClass ?? []]" />
  <span
    v-else-if="!description && !html"
    :class="['text-sm', bothClass ?? [], descriptionClass ?? []]">
    <slot />
  </span>
</template>
